/**
 * FutureClosureEdgeCases - Advanced Edge Case Overrides
 * 
 * Collapsible section for handling forensic accounting edge cases:
 * - Revenue spike detection and handling
 * - Accounting basis (accrual/cash) with lag
 * - Payroll classification (fixed/variable + ICW)
 * - Entity type and expense policies
 * - Data gap warnings
 * - Shutdown/restart cost overrides
 * - Multiplier settings
 */

import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, ChevronUp, AlertTriangle, Settings,
  TrendingUp, DollarSign, Users, Building, Calendar,
  AlertCircle, Info, HelpCircle, FileText, Sparkles, Loader2,
  CheckCircle, Copy
} from 'lucide-react';
import { EdgeCaseConfig, EdgeCaseFlags, EdgeCaseAnalysisResult, analyzeEdgeCases, generateEdgeCaseExplanation, EdgeCaseExplanationResult } from '../services/futureClosureApi';
import { FinancialValue } from './shared/FinancialValue';
import { formatCurrency } from '../utils/financialFormat';

// Type definitions for granular details
interface GrowthMonth {
  month: number;
  month_name: string;
  growth_pct: number;
}

// Tooltip component for edge case explanations
const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => (
  <div className="group relative inline-flex items-center">
    {children}
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 max-w-xs" style={{ backgroundColor: 'var(--color-text-primary)', color: 'var(--color-bg-surface)' }}>
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent" style={{ borderTopColor: 'var(--color-text-primary)' }}></div>
    </div>
  </div>
);

// Tooltip content for each edge case
const TOOLTIPS = {
  revenueSpike: "A >25% year-over-year revenue increase may indicate a one-time event (disaster cleanup, special contract). Excluding it provides a more sustainable baseline for projections.",
  quarterExclusion: "Surgically normalize only the spiked quarters while preserving legitimate seasonality in other quarters.",
  accountingBasis: "Cash-basis businesses record revenue when payment is received, which can lag 15-45 days behind actual service delivery. Adjusting for this lag improves accuracy.",
  payrollMode: "Fixed payroll assumes all staff continue during closure (typical for short closures). Variable assumes non-essential staff are suspended (typical for long closures).",
  icwPayroll: "Increased Cost of Working (ICW) classifies continued payroll as an extra expense incurred to minimize loss, rather than a continuing cost.",
  entityType: "Different business types have different expense structures. Nonprofits typically have 80% variable expenses due to donation volatility.",
  shutdownCosts: "One-time costs incurred when closing (inventory spoilage, equipment storage, contract penalties). NAICS-driven defaults based on industry.",
  restartCosts: "One-time costs to resume operations (re-hiring, re-stocking, marketing). Typically higher than shutdown costs.",
  multiplier: "Some policies allow 2x damages. 'Profits Only' applies to net profit; 'Full Loss' applies to entire business interruption claim."
};

interface FutureClosureEdgeCasesProps {
  reportId: string;
  onConfigChange: (config: EdgeCaseConfig) => void;
  initialConfig?: EdgeCaseConfig;
  entityType?: string;  // From wizard
}

export default function FutureClosureEdgeCases({
  reportId,
  onConfigChange,
  initialConfig,
  entityType: wizardEntityType
}: FutureClosureEdgeCasesProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<EdgeCaseAnalysisResult | null>(null);
  
  // Edge case configuration state
  const [config, setConfig] = useState<EdgeCaseConfig>({
    exclude_spike: initialConfig?.exclude_spike ?? false,
    spike_year: initialConfig?.spike_year ?? null,
    excluded_quarters: initialConfig?.excluded_quarters ?? [],  // NEW: Quarter-specific exclusion
    accounting_basis: initialConfig?.accounting_basis ?? 'accrual',
    lag_days: initialConfig?.lag_days ?? 0,
    payroll_mode: initialConfig?.payroll_mode ?? 'fixed',
    icw_payroll: initialConfig?.icw_payroll ?? false,
    entity_type: initialConfig?.entity_type ?? wizardEntityType as any ?? 'restaurant',
    shutdown_pct: initialConfig?.shutdown_pct ?? null,
    restart_pct: initialConfig?.restart_pct ?? null,
    multiplier_mode: initialConfig?.multiplier_mode ?? 'none'
  });

  // AI Explanation state
  const [explanation, setExplanation] = useState<EdgeCaseExplanationResult | null>(null);
  const [generatingExplanation, setGeneratingExplanation] = useState(false);
  const [explanationExpanded, setExplanationExpanded] = useState(false);

  // Run analysis when expanded or config changes
  useEffect(() => {
    if (isExpanded && reportId) {
      runAnalysis();
    }
  }, [isExpanded, reportId]);

  // Notify parent of config changes
  useEffect(() => {
    onConfigChange(config);
  }, [config, onConfigChange]);

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const result = await analyzeEdgeCases(reportId, config);
      setAnalysisResult(result);
    } catch (error) {
      console.error('Edge case analysis failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = (updates: Partial<EdgeCaseConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  // Generate AI explanation
  const handleGenerateExplanation = async () => {
    setGeneratingExplanation(true);
    try {
      const result = await generateEdgeCaseExplanation(reportId, { edge_case_config: config });
      setExplanation(result);
      setExplanationExpanded(true);
    } catch (error) {
      console.error('Failed to generate explanation:', error);
    } finally {
      setGeneratingExplanation(false);
    }
  };

  const flags = analysisResult?.flags;
  const dualProjections = analysisResult?.dual_projections;

  return (
    <div className="mt-4 border rounded-md overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 transition"
        style={{ backgroundColor: 'var(--color-bg-elevated)' }}
      >
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
          <span className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Advanced Edge Case Overrides</span>
          {flags?.event_anomaly && (
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }}>
              Spike Detected
            </span>
          )}
          {flags?.missing_periods && flags.missing_periods.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
              {flags.missing_periods.length} Data Gaps
            </span>
          )}
        </div>
        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-6" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          {loading && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderBottomColor: 'var(--color-brand)' }}></div>
              <span className="ml-2" style={{ color: 'var(--color-text-secondary)' }}>Analyzing data...</span>
            </div>
          )}

          {!loading && (
            <>
              {/* 1. Revenue Spike Detection */}
              {flags?.event_anomaly && (
                <div className="p-4 border rounded-md bg-[var(--color-bg-surface)]" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>
                          {flags.event_anomaly.year} Revenue Spike Detected (+{flags.event_anomaly.spike_pct}% YoY)
                          {flags.event_anomaly.concentration &&
                           flags.event_anomaly.concentration !== 'full year' &&
                           flags.event_anomaly.concentration !== 'distributed across year' && (
                            <span className="ml-1 font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                              — {flags.event_anomaly.concentration}
                            </span>
                          )}
                        </p>
                        <Tooltip text={TOOLTIPS.revenueSpike}>
                          <HelpCircle className="w-4 h-4 cursor-help" style={{ color: 'var(--color-brand)' }} />
                        </Tooltip>
                      </div>
                      <p className="text-sm mt-1" style={{ color: 'var(--color-brand)' }}>
                        {flags.event_anomaly.reason}
                      </p>

                      {/* P2: Granular Quarter Breakdown */}
                      {flags.event_anomaly.granular_details && (
                        <div className="mt-2 p-2 rounded-md bg-[var(--color-bg-elevated)]">
                          <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-brand)' }}>Quarterly Growth:</p>
                          <div className="flex gap-3 text-xs">
                            {Object.entries(flags.event_anomaly.granular_details.quarterly_growth || {}).map(([quarter, growth]: [string, number]) => {
                              const isConcentrated = flags.event_anomaly?.granular_details?.concentrated_quarters?.includes(quarter);
                              return (
                                <span
                                  key={quarter}
                                  className="px-2 py-0.5 rounded-md"
                                  style={
                                    isConcentrated
                                      ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', fontWeight: 700 }
                                      : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }
                                  }
                                >
                                  {quarter}: {growth > 0 ? '+' : ''}{growth}%
                                </span>
                              );
                            })}
                          </div>
                          {flags.event_anomaly.granular_details.top_growth_months?.length > 0 && (
                            <p className="mt-1 text-xs" style={{ color: 'var(--color-brand)' }}>
                              Top months: {flags.event_anomaly.granular_details.top_growth_months
                                .map((m: GrowthMonth) => `${m.month_name} (+${m.growth_pct}%)`)
                                .join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Exclude/Include Toggle + Quarter-Specific Selection */}
                      <div className="mt-3 space-y-3">
                        {/* Main toggle row */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              // When excluding, default to concentrated quarters if available
                              const defaultQuarters = flags.event_anomaly?.granular_details?.concentrated_quarters || [];
                              updateConfig({ 
                                exclude_spike: true, 
                                spike_year: flags.event_anomaly?.year,
                                excluded_quarters: defaultQuarters.length > 0 ? defaultQuarters : ['Q1', 'Q2', 'Q3', 'Q4']
                              });
                            }}
                            className="px-4 py-2 rounded-md text-sm font-medium transition"
                            style={
                              config.exclude_spike
                                ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                                : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-needs-human-text)' }
                            }
                          >
                            Exclude from Trend
                          </button>
                          <button
                            onClick={() => updateConfig({ exclude_spike: false, spike_year: null, excluded_quarters: [] })}
                            className="px-4 py-2 rounded-md text-sm font-medium transition"
                            style={
                              !config.exclude_spike
                                ? { backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }
                                : { backgroundColor: 'transparent', color: 'var(--color-verified)' }
                            }
                          >
                            Include (Keep As-Is)
                          </button>
                        </div>
                        
                        {/* Quarter-Specific Exclusion Checkboxes - Only show when exclude_spike is true and granular_details available */}
                        {config.exclude_spike && flags.event_anomaly?.granular_details?.quarterly_growth && (
                          <div className="p-3 border rounded-md bg-[var(--color-bg-elevated)]" style={{ borderColor: 'var(--color-border)' }}>
                            <div className="flex items-center gap-2 mb-2">
                              <p className="text-xs font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>
                                Select Quarters to Normalize (surgical exclusion):
                              </p>
                              <Tooltip text={TOOLTIPS.quarterExclusion}>
                                <HelpCircle className="w-3 h-3 cursor-help" style={{ color: 'var(--color-needs-human-text)' }} />
                              </Tooltip>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {['Q1', 'Q2', 'Q3', 'Q4'].map((quarter) => {
                                const growth = flags.event_anomaly?.granular_details?.quarterly_growth?.[quarter] || 0;
                                const isConcentrated = flags.event_anomaly?.granular_details?.concentrated_quarters?.includes(quarter);
                                const isSelected = config.excluded_quarters?.includes(quarter);

                                const toggleQuarter = () => {
                                  const currentQuarters = config.excluded_quarters || [];
                                  const newQuarters = isSelected
                                    ? currentQuarters.filter(q => q !== quarter)
                                    : [...currentQuarters, quarter];
                                  updateConfig({ excluded_quarters: newQuarters });
                                };

                                return (
                                  <button
                                    key={quarter}
                                    onClick={toggleQuarter}
                                    className="px-3 py-1.5 rounded-md text-xs font-medium transition border"
                                    style={
                                      isSelected
                                        ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)', borderColor: 'var(--color-brand)' }
                                        : isConcentrated
                                          ? { backgroundColor: 'var(--color-bg-hover)', color: 'var(--color-brand)', borderColor: 'var(--color-border)' }
                                          : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }
                                    }
                                  >
                                    {quarter}: {growth > 0 ? '+' : ''}{growth}%
                                    {isSelected && ' ✓'}
                                    {isConcentrated && !isSelected && <AlertTriangle size={10} className="inline ml-1" />}
                                  </button>
                                );
                              })}
                            </div>
                            <p className="mt-2 text-xs flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
                              {config.excluded_quarters?.length === 4
                                ? <><FileText size={12} /> All quarters selected (full year normalization)</>
                                : config.excluded_quarters?.length === 0
                                  ? <><AlertTriangle size={12} /> No quarters selected - select at least one</>
                                  : <><CheckCircle size={12} /> Normalizing {config.excluded_quarters?.join(', ')} only</>
                              }
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Dual Projections */}
                      {dualProjections && dualProjections.sustainable_demand && (
                        <div className="mt-3 p-3 rounded-md border bg-[var(--color-bg-elevated)]" style={{ borderColor: 'var(--color-border)' }}>
                          <p className="text-xs font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Live Preview:</p>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span style={{ color: 'var(--color-text-secondary)' }}>Sustainable Baseline:</span>
                              <span className="ml-2 font-bold font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>
                                <FinancialValue value={dualProjections.sustainable_demand} />
                              </span>
                            </div>
                            <div>
                              <span style={{ color: 'var(--color-text-secondary)' }}>Event-Included:</span>
                              <span className="ml-2 font-bold font-mono tabular-nums" style={{ color: 'var(--color-brand)' }}>
                                <FinancialValue value={dualProjections.spiked_demand} />
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 2. Accounting Basis */}
              <div className="p-4 border rounded-md bg-[var(--color-bg-surface)]" style={{ borderColor: 'var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                  <label className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Accounting Basis Override</label>
                  <Tooltip text={TOOLTIPS.accountingBasis}>
                    <HelpCircle className="w-4 h-4 cursor-help" style={{ color: 'var(--color-text-dim)' }} />
                  </Tooltip>
                </div>
                <div className="flex items-center gap-4">
                  <select
                    value={config.accounting_basis}
                    onChange={(e) => updateConfig({
                      accounting_basis: e.target.value as 'accrual' | 'cash',
                      lag_days: e.target.value === 'accrual' ? 0 : config.lag_days
                    })}
                    className="px-3 py-2 border rounded-md text-sm bg-[var(--color-bg-elevated)]"
                    style={{ borderColor: 'var(--color-border)' }}
                  >
                    <option value="accrual">Accrual (Default)</option>
                    <option value="cash">Cash Basis</option>
                  </select>

                  {config.accounting_basis === 'cash' && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Apply lag:</span>
                      <select
                        value={config.lag_days}
                        onChange={(e) => updateConfig({ lag_days: parseInt(e.target.value) })}
                        className="px-3 py-2 border rounded-md text-sm bg-[var(--color-bg-elevated)]"
                        style={{ borderColor: 'var(--color-border)' }}
                      >
                        <option value={0}>No adjustment</option>
                        <option value={15}>15-day lag</option>
                        <option value={30}>30-day lag</option>
                        <option value={45}>45-day lag</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* 3. Payroll Classification */}
              <div className="p-4 border rounded-md bg-[var(--color-bg-surface)]" style={{ borderColor: 'var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                  <label className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Treat Payroll As:</label>
                  <Tooltip text={TOOLTIPS.payrollMode}>
                    <HelpCircle className="w-4 h-4 cursor-help" style={{ color: 'var(--color-text-dim)' }} />
                  </Tooltip>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => updateConfig({ payroll_mode: 'fixed' })}
                    className="px-4 py-2 rounded-md text-sm font-medium transition"
                    style={
                      config.payroll_mode === 'fixed'
                        ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                        : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }
                    }
                  >
                    Continue All Staff (Attorney Mode)
                  </button>
                  <button
                    onClick={() => updateConfig({ payroll_mode: 'variable', icw_payroll: false })}
                    className="px-4 py-2 rounded-md text-sm font-medium transition"
                    style={
                      config.payroll_mode === 'variable'
                        ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                        : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }
                    }
                  >
                    Suspend Non-Essential (Carrier Mode)
                  </button>
                </div>

                {config.payroll_mode === 'fixed' && (
                  <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--color-text-secondary)' }}>
                    <input
                      type="checkbox"
                      checked={config.icw_payroll}
                      onChange={(e) => updateConfig({ icw_payroll: e.target.checked })}
                      className="rounded"
                      style={{ accentColor: 'var(--color-brand)' }}
                    />
                    Flag as Increased Cost of Working (ICW)
                    <Tooltip text={TOOLTIPS.icwPayroll}>
                      <HelpCircle className="w-3 h-3 cursor-help" style={{ color: 'var(--color-text-dim)' }} />
                    </Tooltip>
                  </label>
                )}
              </div>

              {/* 4. Entity Type */}
              <div className="p-4 border rounded-md bg-[var(--color-bg-surface)]" style={{ borderColor: 'var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Building className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                  <label className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Business Type:</label>
                  <Tooltip text={TOOLTIPS.entityType}>
                    <HelpCircle className="w-4 h-4 cursor-help" style={{ color: 'var(--color-text-dim)' }} />
                  </Tooltip>
                </div>
                <select
                  value={config.entity_type}
                  onChange={(e) => updateConfig({ entity_type: e.target.value as any })}
                  className="px-3 py-2 border rounded-md text-sm bg-[var(--color-bg-elevated)]"
                  style={{ borderColor: 'var(--color-border)' }}
                >
                  <option value="restaurant">Restaurant</option>
                  <option value="retail">Retail</option>
                  <option value="factory">Factory/Manufacturing</option>
                  <option value="nonprofit">Nonprofit</option>
                </select>

                {config.entity_type === 'nonprofit' && (
                  <p className="mt-2 text-xs p-2 rounded-md flex items-center gap-1 bg-[var(--color-bg-elevated)]" style={{ color: 'var(--color-brand)' }}>
                    <Info className="w-3 h-3" />
                    80% expenses treated as variable by default (vs 65% for standard businesses)
                  </p>
                )}
              </div>

              {/* 5. Data Gaps Warning */}
              {flags?.missing_periods && flags.missing_periods.length > 0 && (
                <div className="p-4 border rounded-md bg-[var(--color-bg-surface)]" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
                    <div>
                      <p className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Data Gaps Detected</p>
                      <ul className="mt-2 text-sm space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                        {flags.missing_periods.map((gap, i) => (
                          <li key={i}>
                            • {gap.period} missing — using BLS NAICS {gap.naics} proxy ({formatCurrency(gap.amount)}/mo)
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* 6. Shutdown/Restart Costs */}
              <div className="p-4 border rounded-md bg-[var(--color-bg-surface)]" style={{ borderColor: 'var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                  <label className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Closure Cost Overrides</label>
                  <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    (Default: NAICS {config.entity_type})
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1 flex items-center gap-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Shutdown Cost (% of revenue)
                      <Tooltip text={TOOLTIPS.shutdownCosts}>
                        <HelpCircle className="w-3 h-3 cursor-help" style={{ color: 'var(--color-text-dim)' }} />
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      placeholder={`${((flags?.closure_costs?.shutdown_pct ?? 0.15) * 100).toFixed(0)}% (default)`}
                      value={config.shutdown_pct !== null ? (config.shutdown_pct * 100) : ''}
                      onChange={(e) => updateConfig({
                        shutdown_pct: e.target.value ? parseFloat(e.target.value) / 100 : null
                      })}
                      className="w-full px-3 py-2 border rounded-md text-sm bg-[var(--color-bg-elevated)]"
                      style={{ borderColor: 'var(--color-border)' }}
                      min={0}
                      max={100}
                      step={1}
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1 flex items-center gap-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Restart Cost (% of revenue)
                      <Tooltip text={TOOLTIPS.restartCosts}>
                        <HelpCircle className="w-3 h-3 cursor-help" style={{ color: 'var(--color-text-dim)' }} />
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      placeholder={`${((flags?.closure_costs?.restart_pct ?? 0.40) * 100).toFixed(0)}% (default)`}
                      value={config.restart_pct !== null ? (config.restart_pct * 100) : ''}
                      onChange={(e) => updateConfig({
                        restart_pct: e.target.value ? parseFloat(e.target.value) / 100 : null
                      })}
                      className="w-full px-3 py-2 border rounded-md text-sm bg-[var(--color-bg-elevated)]"
                      style={{ borderColor: 'var(--color-border)' }}
                      min={0}
                      max={100}
                      step={1}
                    />
                  </div>
                </div>
              </div>

              {/* 7. Multiplier */}
              <div className="p-4 border rounded-md bg-[var(--color-bg-surface)]" style={{ borderColor: 'var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                  <label className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>Multiplier Application</label>
                  <Tooltip text={TOOLTIPS.multiplier}>
                    <HelpCircle className="w-4 h-4 cursor-help" style={{ color: 'var(--color-text-dim)' }} />
                  </Tooltip>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => updateConfig({ multiplier_mode: 'none' })}
                    className="px-4 py-2 rounded-md text-sm font-medium transition"
                    style={
                      config.multiplier_mode === 'none'
                        ? { backgroundColor: 'var(--color-text-secondary)', color: 'var(--color-on-brand)' }
                        : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }
                    }
                  >
                    None (1×)
                  </button>
                  <button
                    onClick={() => updateConfig({ multiplier_mode: 'profits_only' })}
                    className="px-4 py-2 rounded-md text-sm font-medium transition"
                    style={
                      config.multiplier_mode === 'profits_only'
                        ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                        : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }
                    }
                  >
                    2× Lost Profits Only
                  </button>
                  <button
                    onClick={() => updateConfig({ multiplier_mode: 'full_loss' })}
                    className="px-4 py-2 rounded-md text-sm font-medium transition"
                    style={
                      config.multiplier_mode === 'full_loss'
                        ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                        : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }
                    }
                  >
                    2× Full Loss
                  </button>
                </div>
                {config.multiplier_mode !== 'none' && (
                  <p className="mt-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {config.multiplier_mode === 'profits_only' 
                      ? '2× multiplier applied to lost gross profit only (standard attorney position)'
                      : '2× multiplier applied to total demand including shutdown/restart costs'}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {/* Re-analyze Button */}
                <button
                  onClick={runAnalysis}
                  disabled={loading}
                  className="flex-1 py-3 font-medium rounded-md transition flex items-center justify-center gap-2"
                  style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderBottomColor: 'var(--color-text-secondary)' }}></div>
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Settings className="w-4 h-4" />
                      Re-analyze
                    </>
                  )}
                </button>

                {/* Generate AI Explanation Button */}
                <button
                  onClick={handleGenerateExplanation}
                  disabled={generatingExplanation || !flags}
                  className="flex-1 py-3 font-medium rounded-md transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  {generatingExplanation ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate AI Explanation
                    </>
                  )}
                </button>
              </div>
              
              {/* AI Explanation Display */}
              {explanation && (
                <div className="mt-4 border rounded-md overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
                  <button
                    onClick={() => setExplanationExpanded(!explanationExpanded)}
                    className="w-full px-4 py-3 flex items-center justify-between transition"
                    style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                      <span className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>AI-Generated Explanation</span>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' }}>
                        {explanation.word_count} words
                      </span>
                    </div>
                    {explanationExpanded ? (
                      <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                    ) : (
                      <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                    )}
                  </button>

                  {explanationExpanded && (
                    <div className="p-4" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                      {/* Summary */}
                      {explanation.summary && (
                        <div className="mb-4 p-3 border-l-4 rounded-r-lg bg-[var(--color-bg-elevated)]" style={{ borderLeftColor: 'var(--color-brand)' }}>
                          <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Summary</p>
                          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{explanation.summary}</p>
                        </div>
                      )}

                      {/* Full Explanation */}
                      <div className="prose prose-sm max-w-none">
                        {explanation.explanation_text.split('\n\n').map((paragraph, idx) => (
                          <p key={idx} className="mb-3 whitespace-pre-wrap" style={{ color: 'var(--color-text-secondary)' }}>
                            {paragraph}
                          </p>
                        ))}
                      </div>

                      {/* Copy Button */}
                      <div className="mt-4 pt-4 border-t flex items-center justify-between" style={{ borderColor: 'var(--color-border)' }}>
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                          Generated by {explanation.generated_by}
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(explanation.explanation_text);
                          }}
                          className="px-3 py-1.5 text-sm rounded-md transition flex items-center gap-1"
                          style={{ color: 'var(--color-needs-human-text)' }}
                        >
                          <Copy size={14} /> Copy to Clipboard
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
